import { useCallback, useEffect, useState } from 'react';

// Nothing should keep a map hidden for long once its data is there and the
// fit still has not come. Counted from the end of loading, not from mount: a
// cold reload spends seconds fetching properties and the map style, and a
// timer started at mount ran out before the fit, showing every jump after it.
export const MAP_REVEAL_TIMEOUT_MS = 6000;
// An in-app switch fits well inside this; only a slow, cold load shows the
// placeholder, so the fast path never flashes a spinner.
export const MAP_PLACEHOLDER_DELAY_MS = 300;

const HIDDEN = 'opacity-0 pointer-events-none';
const SHOWN = 'opacity-100';

/**
 * Keeps a property map invisible until it has framed its shapes. The camera
 * jumps two or three times on load (initial view, centroid, fit), none of it
 * animated; hiding the map until the first fit shows one framed map instead.
 *
 * `hasNothingToFit` reveals the map at once when a fit will never come (an
 * empty canvas, a field without geometry). Once `isWaitingForData` is false a
 * timeout reveals it regardless.
 *
 * `showPlaceholder` turns on when the map has been hidden for a moment, so
 * the wait reads as loading, and off the instant the map is revealed.
 */
export default function useMapReveal(
  hasNothingToFit: boolean,
  isWaitingForData = false
): {
  isMapRevealed: boolean;
  showPlaceholder: boolean;
  revealMap: () => void;
  mapRevealClassName: string;
} {
  const [isMapRevealed, setIsMapRevealed] = useState(false);
  const [hasWaited, setHasWaited] = useState(false);
  const revealMap = useCallback(() => setIsMapRevealed(true), []);

  useEffect(() => {
    if (isMapRevealed) return undefined;
    const timeoutId = setTimeout(
      () => setHasWaited(true),
      MAP_PLACEHOLDER_DELAY_MS
    );
    return () => clearTimeout(timeoutId);
  }, [isMapRevealed]);

  useEffect(() => {
    if (hasNothingToFit) revealMap();
  }, [hasNothingToFit, revealMap]);

  useEffect(() => {
    if (isWaitingForData) return undefined;
    const timeoutId = setTimeout(revealMap, MAP_REVEAL_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isWaitingForData, revealMap]);

  return {
    isMapRevealed,
    showPlaceholder: hasWaited && !isMapRevealed,
    revealMap,
    mapRevealClassName: `transition-opacity duration-300 ${
      isMapRevealed ? SHOWN : HIDDEN
    }`
  };
}
